import { useState, useMemo } from 'react';

import { pdf } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

import useEnv from '@zenbusiness/application-commons-env';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';
import { useCustomForm } from '#client/contexts/CustomFormContext';

import * as Styled from './BusinessEntityAdvisorResults.style';
import PoweredByVelo from '../PoweredByVelo/PoweredByVelo';
import { ReportPanel } from '../ReportLayout/ReportPanel';
import SelectionPanel from '../ReportLayout/SelectionPanel';
import SidePanel from '../ReportLayout/SidePanel';
import { BUSINESS_ENTITY_ADVISOR_SECTIONS } from '../utils/constants';
import { BusinessEntityAdvisorResultPDF } from './components/BusinessEntityAdvisorResultPDF/BusinessEntityAdvisorResultPDF';
import { BusinessEntityAdvisorResultsData } from './types';

/**
 * Business Results component - REFACTORED VERSION
 * Displays personalized business ideas and detailed reports
 * Uses enhanced shared ReportPanel
 *
 * This is a refactored version for comparison testing.
 * Original component remains unchanged until verification is complete.
 *
 * @param {object} props - Component props
 * @param {object} props.data - Business results data containing ideas and user profile
 * @param {string[]} [props.actions] - Enabled actions: 'download_pdf', 'generate_more', 'start_over'
 * @param {Function} [props.onComponentClick] - Callback for component actions (follows assistant-ui pattern)
 * @param {object} props.report - Report data
 * @component
 */
function BusinessEntityAdvisorResults({ data, actions = [], report, onComponentClick }) {
  const { DEPLOY_ENV } = useEnv('head-first');
  const { answers, isLoading, setLoading, resetForm, clearAnswers } = useCustomForm();

  const enabledActions = Array.isArray(actions) ? actions : [];
  const canDownloadPdf = enabledActions.includes('download_pdf');
  const canGenerateMore = enabledActions.includes('generate_more');
  const canStartOver = enabledActions.includes('start_over');

  // Support both data.ideas and data.items - memoize to prevent dependency issues
  const items = useMemo(() => data.ideas || data.items || [], [data.ideas, data.items]);

  const [selectedItemId, setSelectedItemId] = useState(data.selectedItemId || items[0]?.id);

  // Find the currently selected idea
  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedItemId),
    [items, selectedItemId]
  );

  const handleItemSelect = (itemId) => {
    setSelectedItemId(itemId);
  };

  const handleDownloadReport = async () => {
    if (isLoading) return;

    setLoading(true);

    try {
      const blob = await pdf(
        <BusinessEntityAdvisorResultPDF environment={DEPLOY_ENV} report={data} />
      ).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.title.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[BusinessEntityAdvisorResults] Download report error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMore = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await onComponentClick({
        type: 'business_results_generate_more',
        payload: {
          answers
        }
      });
    }
    setLoading(false);
  };

  const handleStartOver = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await resetForm();
      await clearAnswers();
      await onComponentClick({ type: 'business_results_start_over' });
    }
    setLoading(false);
  };

  // Handle case where no ideas are provided
  if (!items || items.length === 0) {
    return (
      <Styled.BusinessEntityAdvisorResultsRoot>
        <Box style={{ textAlign: 'center' }}>
          <Text>No recommendation found</Text>
          <Text>
            We weren&apos;t able to generate recommendation based on your responses. Please try
            again.
          </Text>
        </Box>
      </Styled.BusinessEntityAdvisorResultsRoot>
    );
  }

  return (
    <Styled.BusinessEntityAdvisorResultsRoot>
      <Styled.ReportHeader>
        <Text size="md" weight="medium">
          {report.title}
        </Text>
        <Text size="sm">{report.description}</Text>
      </Styled.ReportHeader>
      <Styled.ReportContent>
        <SidePanel
          selectionPanel={
            <SelectionPanel
              clickstreamPrefix="business_entity_advisor"
              generateMoreText="Generate more recommendations"
              getDisplayTitle={(item) => item.title}
              getItemId={(item) => item.id}
              items={items}
              loading={isLoading}
              selectedItemId={selectedItemId}
              startOverText="Start over"
              onGenerateMore={canGenerateMore ? handleGenerateMore : undefined}
              onItemSelect={handleItemSelect}
              onStartOver={canStartOver ? handleStartOver : undefined}
            />
          }
        />

        <ReportPanel
          iconVariant="rounded"
          loading={isLoading}
          reportData={selectedItem}
          sectionsConfig={
            BUSINESS_ENTITY_ADVISOR_SECTIONS.find((item) => item.id === selectedItemId)?.subsections
          }
          onDownloadReport={canDownloadPdf ? handleDownloadReport : undefined}
        />
      </Styled.ReportContent>
      <PoweredByVelo url={ROUTES.website(DEPLOY_ENV)} />
    </Styled.BusinessEntityAdvisorResultsRoot>
  );
}

BusinessEntityAdvisorResults.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.string),
  data: BusinessEntityAdvisorResultsData,
  onComponentClick: PropTypes.func,
  report: PropTypes.object
};

export default BusinessEntityAdvisorResults;
